import * as yup from 'yup';
import { ERegExpTypes } from 'constants/RegExpTypes';

const useLicencesFormSchema = (isEdit: boolean) => {
  const baseSchema = {
    purchaserName: yup
      .string()
      .required('This field is required.')
      .max(80, 'This field should have max 80 characters.')
      .trim(),
    countryUniqueId: yup.string().required('This field is required.'),
    contactJobTitle: yup
      .string()
      .required('This field is required.')
      .max(80, 'This field should have max 80 characters.')
      .trim(),
    contactFullName: yup
      .string()
      .required('This field is required.')
      .max(201, 'This field should have max 201 characters.')
      .trim(),
    contactDepartment: yup
      .string()
      .required('This field is required.')
      .max(80, 'This field should have max 80 characters.')
      .trim(),
    contactEmail: yup
      .string()
      .required('This field is required.')
      .email()
      .max(254, 'This field should have max 254 characters.')
      .trim(),
    contactPhone: yup.string().required('This field is required.').max(15).trim(),
    comments: yup
      .string()
      .nullable()
      .max(80, 'This field should have max 80 characters.')
      .trim(),
    licenceType: yup
      .string()
      .required('This field is required.')
      .max(80, 'This field should have max 80 characters.')
      .trim(),
    startDate: yup
      .string()
      .required('This field is required.')
      .max(80, 'This field should have max 80 characters.')
      .trim(),
    expiryDate: yup
      .string()
      .test('date-validation', 'Expiry date cannot be before start date.', function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;
        return new Date(value) > new Date(startDate);
      }),
    numberOfUsers: yup
      .string()
      .test('number-validation', 'Number of Users cannot be less than 1.', (value: any) =>
        !value ? true : Number(value) > 0,
      ),
    defaultLanguageId: yup.string().required('This field is required.').trim(),
    languagesIds: yup.array(),
    languagesIdsTmp: yup.object(),
  };

  const editSchemaFields = isEdit
    ? {
        licenceAdministratorEmail: yup.mixed().notRequired(),
        licenceAdministratorPassword: yup.string().trim(),
        licenceFirstName: yup.mixed().notRequired(),
        licenceLastName: yup.mixed().notRequired(),
        licencePhonePrefixDictionaryUniqueId: yup.mixed().notRequired(),
        licencePhoneNumber: yup.mixed().notRequired(),
      }
    : {
        licenceAdministratorEmail: yup
          .string()
          .required('This field is required.')
          .email('Email format is invalid.')
          .max(254, 'This field should have max 254 characters.')
          .trim(),
        licenceAdministratorPassword: yup.string().required('This field is required.').trim(),
        licenceFirstName: yup
          .string()
          .required('This field is required.')
          .max(100, 'This field should have max 100 characters.')
          .trim(),
        licenceLastName: yup
          .string()
          .required('This field is required.')
          .max(100, 'This field should have max 100 characters.')
          .trim(),
        licencePhonePrefixDictionaryUniqueId: yup
          .string()
          .required('This field is required.')
          .trim(),
        licencePhoneNumber: yup
          .string()
          .required('This field is required.')
          .matches(new RegExp(ERegExpTypes.onlyDigits), 'Entered value is not correct.')
          .max(15, 'This field should have max 15 characters.')
          .min(5, 'This field should have min 5 characters.')
          .trim(),
      };

  const schemaShape = {
    ...baseSchema,
    ...editSchemaFields,
  };

  return yup.object(schemaShape).test('translationsTest', '', (obj) => {
    if (Object.values(obj.languagesIdsTmp).filter(Boolean).length) return true;
    return new yup.ValidationError('This field is required.', null, 'translations');
  });
};

const useReplaceLicencesFormSchema = () => {
  return yup.object({
    licenceType: yup
      .string()
      .required('This field is required.')
      .max(80, 'This field should have max 80 characters.')
      .trim(),
    startDate: yup
      .string()
      .required('This field is required.')
      .max(80, 'This field should have max 80 characters.')
      .trim(),
    expiryDate: yup
      .string()
      .test('date-validation', 'Expiry date cannot be before start date.', function (value) {
        const { startDate } = this.parent;
        if (!value || !startDate) return true;
        return new Date(value) > new Date(startDate);
      }),
    numberOfUsers: yup
      .string()
      .test('number-validation', 'Number of Users cannot be less than 1.', (value: any) => {
        if (!value || typeof value !== 'string') return true;
        return Number(value) > 0;
      }),
    licenceAdministratorEmail: yup
      .string()
      .email('Email format is invalid.')
      .required('This field is required.')
      .max(254, 'This field should have max 254 characters.')
      .trim(),
    licenceAdministratorPassword: yup.string().required('This field is required.').trim(),
    licenceFirstName: yup
      .string()
      .required('This field is required.')
      .max(100, 'This field should have max 100 characters.')
      .trim(),
    licenceLastName: yup
      .string()
      .required('This field is required.')
      .max(100, 'This field should have max 100 characters.')
      .trim(),
    licencePhonePrefixDictionaryUniqueId: yup
      .string()
      .required('This field is required.')
      .trim(),
    licencePhoneNumber: yup
      .string()
      .required('This field is required.')
      .matches(new RegExp(ERegExpTypes.onlyDigits), 'Entered value is not correct.')
      .max(15, 'This field should have max 15 characters.')
      .min(5, 'This field should have min 5 characters.')
      .trim(),
  });
};

export { useLicencesFormSchema, useReplaceLicencesFormSchema };
